import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { useStore } from '../store/useStore';
import { useShallow } from 'zustand/react/shallow';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { EntityModal } from './entity-modal';
import { FeedbackModal } from './FeedbackModal';
import { OnboardingWelcomeModal } from './OnboardingWelcomeModal';
import { OnboardingTour, TourStep } from './OnboardingTour';
import { OnboardingTourClosingModal } from './OnboardingTourClosingModal';
import { ErrorBoundary } from './ErrorBoundary';
import GoalCompletionModal from '../components/GoalCompletionModal';
import { onboardingStorage, useOnboarding } from '../hooks/useOnboarding';
import { OnboardingFlow } from './onboarding/OnboardingFlow';
import { OnboardingBanner } from './onboarding/OnboardingBanner';
import { SpotlightOverlay } from './onboarding/SpotlightOverlay';
import { trackEvent } from '../lib/analytics';
import { ROUTES } from '../routes';
import { BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// ─── Tour step view routing ──────────────────────────────────────────────────

// Map tour step view identifiers to route paths
const VIEW_TO_ROUTE: Record<string, string> = {
  dashboard: ROUTES.dashboard,
  tasks: ROUTES.tasks,
  canvas: ROUTES.brain,
};

// Map route paths to view identifiers (for tour matching)
const ROUTE_TO_VIEW: Record<string, string> = Object.fromEntries(
  Object.entries(VIEW_TO_ROUTE).map(([k, v]) => [v, k])
);

export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const session = useAuthStore((s) => s.session);

  // Derive current "view" from URL path for tour matching
  const currentView = ROUTE_TO_VIEW[location.pathname] ?? location.pathname.slice(1);

  const TOUR_STEPS: TourStep[] = [
    {
      id: 'dashboard-welcome',
      view: 'dashboard',
      title: t('onboarding.tour_step_dashboard_title'),
      body: t('onboarding.tour_step_dashboard_body'),
      target: 'dashboard-quick-add',
      placement: 'bottom',
    },
    {
      id: 'tasks-structured',
      view: 'tasks',
      title: t('onboarding.tour_step_typed_objects_title'),
      body: t('onboarding.tour_step_typed_objects_body'),
      target: 'tasks-new-btn',
      placement: 'bottom',
    },
    {
      id: 'canvas-intro',
      view: 'canvas',
      title: t('onboarding.tour_step_canvas_title'),
      body: t('onboarding.tour_step_canvas_body'),
      variant: 'featured',
    },
    {
      id: 'canvas-elements',
      view: 'canvas',
      title: t('onboarding.tour_step_elements_title'),
      body: t('onboarding.tour_step_elements_body'),
      target: 'canvas-elements-sidebar',
      placement: 'right',
    },
    {
      id: 'tour-done',
      view: null,
      title: t('onboarding.tour_step_ready_title'),
      body: t('onboarding.tour_step_ready_body'),
    },
  ];

  // ─── Phase 3.5 — New onboarding flow ─────────────────────────────────
  const {
    isOnboardingPending,
    isLoading: isOnboardingLoading,
    completeOnboarding,
    dismissOnboarding,
  } = useOnboarding();

  // Access au store pour ouvrir/détecter l'EntityModal
  const { modalState, openModal, pendingCompletedGoalId, setPendingCompletedGoalId } = useStore(
    useShallow((s) => ({
      modalState: s.modalState,
      openModal: s.openModal,
      pendingCompletedGoalId: s.pendingCompletedGoalId,
      setPendingCompletedGoalId: s.setPendingCompletedGoalId,
    }))
  );

  // Phase de l'onboarding interactif
  const [onboardingPhase, setOnboardingPhase] = useState<
    'modal' | 'entity-creation' | 'spotlight' | null
  >(null);
  const [entityModalOpened, setEntityModalOpened] = useState(false);

  // ─── Onboarding state ───────────────────────────────────────────────────
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [tourInProgress, setTourInProgress] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);

  const activeTourStepId = tourActive ? TOUR_STEPS[tourStepIndex]?.id : undefined;

  // Show welcome modal or restore tour state on login/refresh
  useEffect(() => {
    if (session) {
      const welcomed = onboardingStorage.isWelcomeSeen();
      const tourDone = onboardingStorage.isTourDone() || onboardingStorage.isChecklistDone();
      if (!welcomed) {
        setShowWelcomeModal(true);
      } else if (!tourDone) {
        const savedStep = onboardingStorage.getTourStep();
        setTourStepIndex(savedStep);
        setTourInProgress(true);
      }
    }
  }, [session]);

  // Initialisation de la phase onboarding
  useEffect(() => {
    if (!isOnboardingLoading && isOnboardingPending && onboardingPhase === null) {
      setOnboardingPhase('modal');
    }
  }, [isOnboardingLoading, isOnboardingPending, onboardingPhase]);

  // Callback onAdvanceToInteractive
  const handleAdvanceToInteractive = useCallback(() => {
    setOnboardingPhase('entity-creation');
  }, []);

  // Auto-ouverture de l'EntityModal
  useEffect(() => {
    if (onboardingPhase === 'entity-creation' && !entityModalOpened) {
      const timer = setTimeout(() => {
        openModal('create');
        setEntityModalOpened(true);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [onboardingPhase, entityModalOpened, openModal]);

  // Détection fermeture EntityModal → passer au spotlight
  useEffect(() => {
    if (onboardingPhase === 'entity-creation' && entityModalOpened && !modalState.isOpen) {
      navigate('/brain');
      // Petit délai pour laisser la page /brain se charger
      setTimeout(() => {
        setOnboardingPhase('spotlight');
      }, 500);
    }
  }, [onboardingPhase, entityModalOpened, modalState.isOpen, navigate]);

  // Spotlight complete
  const handleSpotlightComplete = useCallback(async () => {
    trackEvent('onboarding_complete');
    await completeOnboarding();
    setOnboardingPhase(null);
    setEntityModalOpened(false);
  }, [completeOnboarding]);

  // Skip onboarding interactif
  const handleSkipInteractive = useCallback(() => {
    trackEvent('onboarding_skip');
    dismissOnboarding();
    setOnboardingPhase(null);
    setEntityModalOpened(false);
  }, [dismissOnboarding]);

  // Auto-resume: activate tour when the user reaches the right page
  useEffect(() => {
    if (!tourInProgress || tourActive) return;
    const step = TOUR_STEPS[tourStepIndex];
    if (step?.view === null || (step?.view && currentView === step.view)) {
      setTourActive(true);
    }
  }, [currentView, tourInProgress, tourActive, tourStepIndex]);

  // Close tour if user manually navigates away from the expected view
  useEffect(() => {
    if (!tourActive) return;
    const step = TOUR_STEPS[tourStepIndex];
    if (step?.view === null) return;
    if (step?.view && currentView !== step.view) {
      setTourActive(false);
    }
  }, [currentView, tourActive, tourStepIndex]);

  const handleWelcomeStart = useCallback(() => {
    onboardingStorage.setWelcomeSeen();
    onboardingStorage.setTourStep(0);
    setShowWelcomeModal(false);
    setTourStepIndex(0);
    setTourInProgress(true);
    setTourActive(true);
    const firstView = TOUR_STEPS[0]?.view;
    if (firstView && VIEW_TO_ROUTE[firstView]) navigate(VIEW_TO_ROUTE[firstView]);
  }, [navigate]);

  const handleWelcomeSkip = useCallback(() => {
    onboardingStorage.setWelcomeSeen();
    onboardingStorage.setTourDone();
    setShowWelcomeModal(false);
  }, []);

  const handleTourNext = useCallback(() => {
    const nextIndex = tourStepIndex + 1;
    if (nextIndex >= TOUR_STEPS.length) {
      onboardingStorage.setTourDone();
      setTourActive(false);
      setTourInProgress(false);
      return;
    }
    const nextStep = TOUR_STEPS[nextIndex];
    if (nextStep.view && VIEW_TO_ROUTE[nextStep.view]) navigate(VIEW_TO_ROUTE[nextStep.view]);
    onboardingStorage.setTourStep(nextIndex);
    setTourStepIndex(nextIndex);
  }, [tourStepIndex, navigate]);

  const handleTourPrev = useCallback(() => {
    const prevIndex = tourStepIndex - 1;
    if (prevIndex < 0) return;
    const prevStep = TOUR_STEPS[prevIndex];
    if (prevStep.view && VIEW_TO_ROUTE[prevStep.view]) navigate(VIEW_TO_ROUTE[prevStep.view]);
    onboardingStorage.setTourStep(prevIndex);
    setTourStepIndex(prevIndex);
  }, [tourStepIndex, navigate]);

  const handleTourSkip = useCallback(() => {
    onboardingStorage.setTourDone();
    setTourActive(false);
    setTourInProgress(false);
  }, []);

  const handleTourResume = useCallback(() => {
    const step = TOUR_STEPS[tourStepIndex];
    if (step?.view && VIEW_TO_ROUTE[step.view]) navigate(VIEW_TO_ROUTE[step.view]);
    setTourActive(true);
  }, [tourStepIndex, navigate]);

  const SPOTLIGHT_STEPS = [
    {
      targetSelector: '[data-onboarding="sidebar-synapse"]',
      title: t('onboarding.spotlight_synapse_title'),
      description: t('onboarding.spotlight_synapse_desc'),
      tooltipPosition: 'right' as const,
    },
    {
      targetSelector: '[data-onboarding="sidebar-brain"]',
      title: t('onboarding.spotlight_brain_title'),
      description: t('onboarding.spotlight_brain_desc'),
      tooltipPosition: 'right' as const,
    },
    {
      targetSelector: '[data-onboarding="sidebar-views"]',
      title: t('onboarding.spotlight_views_title'),
      description: t('onboarding.spotlight_views_desc'),
      tooltipPosition: 'right' as const,
    },
    {
      targetSelector: '[data-onboarding="header-new"]',
      title: t('onboarding.spotlight_create_title'),
      description: t('onboarding.spotlight_create_desc'),
      tooltipPosition: 'bottom' as const,
    },
  ];

  // Determine if current route is /brain (full-bleed, no padding wrapper)
  const isCanvasRoute = location.pathname === ROUTES.brain;
  // Full-width routes bypass the max-w-6xl wrapper (kanban, gantt, finances)
  const isFullWidthRoute =
    location.pathname === ROUTES.kanban ||
    location.pathname === ROUTES.gantt ||
    location.pathname === ROUTES.finances;

  return (
    <>
      <ErrorBoundary>
        <div className="flex h-screen overflow-hidden bg-zinc-950 text-zinc-100 selection:bg-zinc-800 selection:text-zinc-100">
          <Sidebar />
          <div className="flex-1 flex flex-col overflow-hidden relative">
            <Header />
            <main
              className={`flex-1 relative flex flex-col ${isFullWidthRoute || isCanvasRoute ? 'overflow-hidden' : 'overflow-y-auto'}`}
            >
              {isCanvasRoute || isFullWidthRoute ? (
                <Outlet context={{ activeTourStep: activeTourStepId }} />
              ) : (
                <div className="p-6 md:p-8 lg:p-10 max-w-6xl mx-auto w-full h-full">
                  <Outlet context={{ activeTourStep: activeTourStepId }} />
                </div>
              )}
            </main>
          </div>
          <EntityModal />
          <FeedbackModal />
          <OnboardingWelcomeModal
            isOpen={showWelcomeModal}
            onStart={handleWelcomeStart}
            onSkip={handleWelcomeSkip}
          />
          {tourActive && tourStepIndex < TOUR_STEPS.length - 1 && (
            <OnboardingTour
              steps={TOUR_STEPS}
              currentIndex={tourStepIndex}
              onNext={handleTourNext}
              onPrev={handleTourPrev}
              onSkip={handleTourSkip}
            />
          )}
          {tourActive && tourStepIndex === TOUR_STEPS.length - 1 && (
            <OnboardingTourClosingModal onDone={handleTourNext} onPrev={handleTourPrev} />
          )}
          {tourInProgress && !tourActive && !showWelcomeModal && (
            <button
              onClick={handleTourResume}
              className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-3 py-2 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 text-xs shadow-lg transition-colors"
            >
              <BookOpen className="w-3.5 h-3.5" />
              {t('onboarding.resume_onboarding')}
            </button>
          )}
        </div>
        {!isOnboardingLoading && isOnboardingPending && onboardingPhase === 'modal' && (
          <OnboardingFlow
            onComplete={completeOnboarding}
            onDismiss={() => {
              trackEvent('onboarding_skip');
              dismissOnboarding();
              setOnboardingPhase(null);
            }}
            onAdvanceToInteractive={handleAdvanceToInteractive}
          />
        )}
        {onboardingPhase === 'entity-creation' && (
          <OnboardingBanner
            stepLabel={t('onboarding.banner_step_label')}
            message={t('onboarding.banner_create_entity')}
            onSkip={handleSkipInteractive}
          />
        )}
        {/* Onboarding Phase: Spotlight discovery (étape 4) */}
        {onboardingPhase === 'spotlight' && (
          <SpotlightOverlay
            steps={SPOTLIGHT_STEPS}
            onComplete={handleSpotlightComplete}
            onSkip={handleSkipInteractive}
          />
        )}
        {pendingCompletedGoalId && (
          <GoalCompletionModal
            goalId={pendingCompletedGoalId}
            onClose={() => setPendingCompletedGoalId(null)}
          />
        )}
      </ErrorBoundary>
    </>
  );
}
